"use client";

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { faChevronDown, FontAwesomeIcon } from '@/constants/icons';
import { getBaseImageUrl, getEventsHref, getMerchantHref, getPromotionHref } from '@/constants/hooks';

interface Props {
  merchantData: any[];
  categories: any[];
  mer_slug: string;
  mer_slug_type: string;
  cat_slug: string;
  events: any[];
  promotions: any[];
  promo_slug: string;
  blog_title?: string;
  blog_url?: string;
  companyDomain: string;
}

export default function NavigationPill({
  merchantData,
  categories,
  mer_slug,
  mer_slug_type,
  cat_slug,
  events,
  promotions,
  promo_slug,
  blog_title,
  blog_url,
  companyDomain,
}: Props) {
  const pathname = usePathname() || '';

  const getNavClass = (isActive: boolean) => {
    return `flex items-center gap-1.5 px-3 py-2 text-[14px] font-bold font-sans tracking-wide transition-all ${isActive
        ? 'text-[#F97316] border-b-2 border-[#F97316]'
        : 'text-[#1E3A5F] hover:text-[#F97316]'
      }`;
  };

  return (
    <nav className="flex items-center gap-0.5 overflow-visible">
      <Link href="/" className={getNavClass(pathname === '/')}>
        Home
      </Link>

      <div className="group relative">
        <Link href={`/all-stores`} className={getNavClass(pathname.startsWith('/all-stores'))}>
          Stores
          <FontAwesomeIcon icon={faChevronDown} className="w-2.5 h-2.5 transition-transform duration-200 group-hover:rotate-180 text-[#4B5563]" />
        </Link>
        {/* Dropdown Stores */}
        <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 w-max z-[100]">
          {/* Arrow caret */}
          <div className="absolute top-[7px] left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-l border-t border-[#E5E7EB] rotate-45" />
          <div className="bg-white rounded-2xl shadow-[0_16px_48px_-8px_rgba(30,58,95,0.18)] border border-[#E5E7EB] p-4 w-[460px] flex flex-wrap gap-2 mt-[6px]">
            {merchantData?.length > 0 ? (
              merchantData.slice(0, 10).map((item, i) => (
                <Link key={i} href={getMerchantHref(item, mer_slug, mer_slug_type)} className="flex items-center gap-3 w-[calc(50%-0.25rem)] hover:bg-[#F8FAFC] p-2 rounded-xl transition-colors group/item">
                  <span className="w-9 h-9 bg-white rounded-xl border border-[#E5E7EB] shadow-sm overflow-hidden flex-shrink-0 relative flex items-center justify-center">
                    {item?.merchant_logo ?
                      <Image src={getBaseImageUrl(companyDomain, item?.merchant_logo, "")} alt={item?.merchant_name} layout="fill" objectFit="contain" className="p-1" />
                      : <span className="text-[#4B5563] text-xs text-center leading-[9px]">no logo</span>
                    }
                  </span>
                  <span className="text-[13px] font-semibold text-[#1E3A5F] group-hover/item:text-[#F97316] truncate transition-colors">{item.merchant_name}</span>
                </Link>
              ))
            ) : (
              <div className="text-sm text-gray-500 p-2 w-full text-center">No stores available</div>
            )}
            {merchantData?.length > 10 && (
              <Link href={`/all-stores`} className="w-full text-center mt-2 py-2 text-[13px] font-bold text-[#F97316] hover:text-white bg-[#F8FAFC] hover:bg-[#F97316] transition-colors rounded-xl">
                View All Stores
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="group relative">
        <Link href={`/${cat_slug}`} className={getNavClass(pathname.includes(`/${cat_slug}`))}>
          Categories
          <FontAwesomeIcon icon={faChevronDown} className="w-2.5 h-2.5 transition-transform duration-200 group-hover:rotate-180 text-[#4B5563]" />
        </Link>
        {/* Dropdown Categories */}
        <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 w-max" style={{ zIndex: 99999 }}>
          {/* Arrow caret */}
          <div className="absolute top-[7px] left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-l border-t border-[#E5E7EB] rotate-45" />
          <div className="bg-white rounded-2xl shadow-[0_16px_48px_-8px_rgba(30,58,95,0.18)] border border-[#E5E7EB] p-4 w-[460px] flex flex-wrap gap-2 mt-[6px]">
            {categories?.length > 0 ? (
              categories.slice(0, 10).map((item: any, i: number) => (
                <Link key={i} href={`/${item?.url}`} className="flex items-center gap-3 w-[calc(50%-0.25rem)] hover:bg-[#F8FAFC] p-2 rounded-xl transition-colors group/item">
                  <span className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#F8FAFC] border border-[#E5E7EB] p-1 flex-shrink-0">
                    <Image src={getBaseImageUrl(companyDomain, item?.category_image, "")} alt={item?.name} height={20} width={20} className="object-contain" />
                  </span>
                  <span className="text-[13px] font-semibold text-[#1E3A5F] group-hover/item:text-[#F97316] truncate transition-colors">{item.name}</span>
                </Link>
              ))
            ) : (
              <div className="text-sm text-[#4B5563] p-2 w-full text-center">No categories available</div>
            )}
          </div>
        </div>
      </div>

      <div className="group relative">
        <Link href="/all-products" className={getNavClass(pathname.startsWith('/all-products') || pathname.startsWith('/products'))}>
          Products
          <FontAwesomeIcon icon={faChevronDown} className="w-2.5 h-2.5 transition-transform duration-200 group-hover:rotate-180 text-[#4B5563]" />
        </Link>
        <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 w-max" style={{ zIndex: 99999 }}>
          {/* Arrow caret */}
          <div className="absolute top-[7px] left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-l border-t border-[#E5E7EB] rotate-45" />
          <div className="bg-white rounded-2xl shadow-[0_16px_48px_-8px_rgba(30,58,95,0.18)] border border-[#E5E7EB] p-2 flex flex-col gap-1 min-w-[220px] mt-[6px]">
            <Link href="/products" className="hover:bg-[#F8FAFC] p-2.5 rounded-xl transition-colors text-[13px] font-semibold text-[#1E3A5F] hover:text-[#F97316]">
              Brands Products
            </Link>
          </div>
        </div>
      </div>

      {events?.length > 0 && (
        <div className="group relative">
          <Link href={`/events`} className={getNavClass(pathname.startsWith('/events'))}>
            Events
            <FontAwesomeIcon icon={faChevronDown} className="w-2.5 h-2.5 transition-transform duration-200 group-hover:rotate-180 text-[#4B5563]" />
          </Link>
          <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 w-max" style={{ zIndex: 99999 }}>
            {/* Arrow caret */}
            <div className="absolute top-[7px] left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-l border-t border-[#E5E7EB] rotate-45" />
            <div className="bg-white rounded-2xl shadow-[0_16px_48px_-8px_rgba(30,58,95,0.18)] border border-[#E5E7EB] p-2 flex flex-col gap-1 min-w-[220px] mt-[6px]">
              {events.slice(0, 5).map((item, i) => (
                <Link key={i} href={getEventsHref(item, "slug")} className="hover:bg-[#F8FAFC] p-2.5 rounded-xl transition-colors text-[13px] font-semibold text-[#1E3A5F] hover:text-[#F97316]">
                  {item.name}
                </Link>
              ))}
              {events.length > 5 && (
                <Link href={`/events`} className="hover:bg-[#DCFCE7] p-2.5 text-center rounded-xl transition-colors text-[12px] font-bold text-[#F97316] mt-1">View All Events</Link>
              )}
            </div>
          </div>
        </div>
      )}

      {promotions?.length > 0 && (
        <div className="group relative">
          <Link href={`/${promo_slug}`} className={getNavClass(pathname.includes(`/${promo_slug}`))}>
            Promotions
            <FontAwesomeIcon icon={faChevronDown} className="w-2.5 h-2.5 transition-transform duration-200 group-hover:rotate-180 text-[#4B5563]" />
          </Link>
          <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 w-max" style={{ zIndex: 99999 }}>
            {/* Arrow caret */}
            <div className="absolute top-[7px] left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-l border-t border-[#E5E7EB] rotate-45" />
            <div className="bg-white rounded-2xl shadow-[0_16px_48px_-8px_rgba(30,58,95,0.18)] border border-[#E5E7EB] p-2 flex flex-col gap-1 min-w-[220px] mt-[6px]">
              {promotions.slice(0, 5).map((item, i) => (
                <Link key={i} href={getPromotionHref(item, promo_slug)} className="hover:bg-[#F8FAFC] p-2.5 rounded-xl transition-colors text-[13px] font-semibold text-[#1E3A5F] hover:text-[#F97316]">
                  {item.name}
                </Link>
              ))}
              {promotions.length > 5 && (
                <Link href={`/${promo_slug}`} className="hover:bg-[#DCFCE7] p-2.5 text-center rounded-xl transition-colors text-[12px] font-bold text-[#F97316] mt-1">View All Deals</Link>
              )}
            </div>
          </div>
        </div>
      )}

      {blog_title && blog_url && (
        <Link href={blog_url} className={getNavClass(pathname === blog_url)}>
          {blog_title}
        </Link>
      )}
    </nav>
  );
}
